import apiClient from "./client";

// GET /api/routes/ → returns an array of Route objects with pagination support
export const fetchRoutes = async (page = 1, pageSize = 10, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    ...filters,
  };

  const { data } = await apiClient.get("api/routes/", { params });

  // keep your existing response normalization exactly as-is...
  if (data.results !== undefined) {
    const fc = data.results;

    if (fc?.type === "FeatureCollection" && fc?.features) {
      return {
        results: fc.features.map((f) => ({
          id: f.id,
          name: f.properties.name,
          file: f.properties.file,
          uploaded_at: f.properties.uploaded_at,
          video: f.properties.video,
          distance_km: f.properties.distance_km,
          duration_seconds: f.properties.duration_seconds,
          status: f.properties.status,
          star_rating: f.properties.star_rating,
          surface_rating_name: f.properties.surface_rating_name,
          weighted_malfunction_score: f.properties.weighted_malfunction_score,
          weighted_malfunction_points_per_km: f.properties.weighted_malfunction_points_per_km,
          surface_rating_details: f.properties.surface_rating_details,
          zenodo_doi: f.properties.zenodo_doi,
          zenodo_published_at: f.properties.zenodo_published_at,
          uploader_role: f.properties.uploader_role,
          tags: f.properties.tags || [],
          geom: f.geometry,
        })),
        count: data.count,
        next: data.next,
        previous: data.previous,
      };
    }

    return {
      results: Array.isArray(fc) ? fc : [],
      count: data.count,
      next: data.next,
      previous: data.previous,
    };
  }

  // fallback unchanged...
  const fc = data;
  if (fc?.type === "FeatureCollection" && fc?.features) {
    return {
      results: fc.features.map((f) => ({
        id: f.id,
        name: f.properties.name,
        file: f.properties.file,
        uploaded_at: f.properties.uploaded_at,
        video: f.properties.video,
        distance_km: f.properties.distance_km,
        duration_seconds: f.properties.duration_seconds,
        status: f.properties.status,
        star_rating: f.properties.star_rating,
        surface_rating_name: f.properties.surface_rating_name,
        weighted_malfunction_score: f.properties.weighted_malfunction_score,
        weighted_malfunction_points_per_km: f.properties.weighted_malfunction_points_per_km,
        surface_rating_details: f.properties.surface_rating_details,
        zenodo_doi: f.properties.zenodo_doi,
        zenodo_published_at: f.properties.zenodo_published_at,
        uploader_role: f.properties.uploader_role,
        tags: f.properties.tags || [],
        geom: f.geometry,
      })),
      count: fc.features.length,
      next: null,
      previous: null,
    };
  }

  return {
    results: Array.isArray(data) ? data : [],
    count: Array.isArray(data) ? data.length : 0,
    next: null,
    previous: null,
  };
};

// POST /api/routes/ (multipart/form-data)
export const uploadRoute = async ({ file, videoId = null, name = "" }) => {
  const form = new FormData();
  form.append("file", file);

  if (videoId !== null) {
    form.append("video", videoId);
  }

  if (name && name.trim()) {
    form.append("name", name.trim());
  }

  const { data } = await apiClient.post("api/routes/", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return data;
};

// update route metadata
export const updateRoute = async ({ id, tags, video }) => {
  const payload = {};
  if (tags !== undefined) payload.tags = tags;
  if (video !== undefined) payload.video = video;
  const { data } = await apiClient.patch(`api/routes/${id}/`, payload);
  return data;
};

export const deleteRoute = async (id) => {
  await apiClient.delete(`api/routes/${id}/`);
  return id;
};

// GET /api/routes/:id → returns a single Route object
export const fetchRouteById = async (id) => {
  const { data } = await apiClient.get(`api/routes/${id}/`);
  
  // Handle GeoJSON Feature
  if (data.type === "Feature") {
    return {
      id: data.id,
      name: data.properties.name,
      file: data.properties.file,
      uploaded_at: data.properties.uploaded_at,
      video: data.properties.video,
      distance_km: data.properties.distance_km,
      duration_seconds: data.properties.duration_seconds,
      status: data.properties.status,
      star_rating: data.properties.star_rating,
      surface_rating_name: data.properties.surface_rating_name,
      weighted_malfunction_score: data.properties.weighted_malfunction_score,
      weighted_malfunction_points_per_km: data.properties.weighted_malfunction_points_per_km,
      surface_rating_details: data.properties.surface_rating_details,
      tags: data.properties.tags || [],
      geom: data.geometry,
    };
  }
  
  return data;
};

const routeFromFeature = (feature) => ({
  id: feature.id,
  name: feature.properties.name,
  file: feature.properties.file,
  uploaded_at: feature.properties.uploaded_at,
  video: feature.properties.video,
  distance_km: feature.properties.distance_km,
  duration_seconds: feature.properties.duration_seconds,
  status: feature.properties.status,
  star_rating: feature.properties.star_rating,
  surface_rating_name: feature.properties.surface_rating_name,
  weighted_malfunction_score: feature.properties.weighted_malfunction_score,
  weighted_malfunction_points_per_km: feature.properties.weighted_malfunction_points_per_km,
  surface_rating_details: feature.properties.surface_rating_details,
  zenodo_doi: feature.properties.zenodo_doi,
  zenodo_published_at: feature.properties.zenodo_published_at,
  uploader_role: feature.properties.uploader_role,
  tags: feature.properties.tags || [],
  geom: feature.geometry,
});

// GET /api/routes/map-data/ → all accessible routes with map-only fields.
export const fetchRouteMapData = async (filters = {}) => {
  const { data } = await apiClient.get("api/routes/map-data/", {
    params: filters,
  });

  if (data?.type === "FeatureCollection" && Array.isArray(data.features)) {
    return data.features.map(routeFromFeature);
  }

  return [];
};

export const fetchRouteByVideoId = async (videoId) => {
  if (!videoId) return null;

  const { data } = await apiClient.get("api/routes/", {
    params: {
      video: videoId,
      page_size: 1,
    },
  });

  const featureCollection = data?.results?.type === "FeatureCollection"
    ? data.results
    : data?.type === "FeatureCollection"
      ? data
      : null;

  const feature = featureCollection?.features?.[0];
  if (feature) return routeFromFeature(feature);

  if (Array.isArray(data?.results)) return data.results[0] ?? null;
  if (Array.isArray(data)) return data[0] ?? null;

  return null;
};

// GET /api/routes/stats/ → { count, total_distance_km }
export const fetchRouteStats = async () => {
  const { data } = await apiClient.get("api/routes/stats/");
  return {
    count: Number(data?.count ?? 0),
    total_distance_km: Number(data?.total_distance_km ?? 0),
  };
};
